import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import {
  ICheckTransaction2faData,
  ITransaction2faApiResponse,
  TTransaction2faType,
} from './transaction2faTypes';

interface ICheckTransaction2faInput {
  type: TTransaction2faType;
  amount: number;
}

const initialState: IApiState<
  ITransaction2faApiResponse<ICheckTransaction2faData>
> = {
  value: null,
  status: 'idle',
  error: '',
};

export const checkTransaction2faAsync = createAsyncThunk(
  'transaction/check-2fa',
  async (
    { type, amount }: ICheckTransaction2faInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<
        ITransaction2faApiResponse<ICheckTransaction2faData>
      >(
        '/business/transaction/check-2fa',
        { type, amount },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<
        ITransaction2faApiResponse<ICheckTransaction2faData>
      >;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const checkTransaction2faSlice = createSlice({
  name: 'transaction/check-2fa',
  initialState,
  reducers: {
    resetCheckTransaction2fa: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkTransaction2faAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(checkTransaction2faAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(checkTransaction2faAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ITransaction2faApiResponse<
          ICheckTransaction2faData
        >;
        state.error = action.error.message;
      });
  },
});

export const { resetCheckTransaction2fa } = checkTransaction2faSlice.actions;

export default checkTransaction2faSlice.reducer;
